import dayjs from "dayjs";
import type { InsiderDetail, InsiderTradeDetail, NotificationDetail } from "./types";

const integer = new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 });
const price = new Intl.NumberFormat("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const compact = new Intl.NumberFormat("en-US", { notation: "compact", maximumFractionDigits: 1 });
const compactHolding = new Intl.NumberFormat("en-US", { notation: "compact", maximumFractionDigits: 2 });

/** "$16.7M", "$1.5K"; under a thousand, whole dollars ("$266"). */
export const formatUsd = (value: number): string =>
  Math.abs(value) < 1000 ? `$${integer.format(value)}` : `$${compact.format(value)}`;

/** A headline in parts, so the amounts can carry the colour of their side. */
export type HeadlinePart = { text: string; tone: "plain" | "buy" | "sell" };

export interface InsiderSummary {
  headline: HeadlinePart[];
  facts: string;
  footnote: string | null;
}

const isSide = (value: unknown): value is { count: number; value: number } =>
  typeof value === "object" &&
  value !== null &&
  typeof (value as { count?: unknown }).count === "number" &&
  typeof (value as { value?: unknown }).value === "number";

// Every field the summary reads: a server that changes the shape falls back
// to the row's heading and body, never a crash in a native build in the wild.
export const isInsiderDetail = (detail: NotificationDetail | null | undefined): detail is InsiderDetail => {
  if (detail?.kind !== "insider") return false;
  const insider = detail as Partial<InsiderDetail>;
  return (
    Array.isArray(insider.trades) &&
    isSide(insider.buys) &&
    isSide(insider.sells) &&
    (insider.insiders === null || typeof insider.insiders === "number") &&
    typeof insider.otherCount === "number"
  );
};

const plural = (count: number, singular: string, pluralForm = `${singular}s`): string =>
  `${integer.format(count)} ${count === 1 ? singular : pluralForm}`;

const tradeAt = (trade: InsiderTradeDetail): string =>
  `${integer.format(trade.shares)} ${trade.shares === 1 ? "share" : "shares"}${
    trade.price !== null ? ` at $${price.format(trade.price)}` : ""
  }`;

const verbOf = (trade: InsiderTradeDetail): string => (trade.side === "buy" ? "bought" : "sold");

// "1 RSU conversion, 2 gifts" from the rows the payload kept, and how many it didn't.
const otherActivity = (detail: InsiderDetail): string | null => {
  const shown = detail.trades.filter((trade) => trade.side === "other");
  const counts = new Map<string, number>();
  for (const trade of shown) counts.set(trade.label, (counts.get(trade.label) ?? 0) + 1);
  const named = Array.from(counts.entries()).map(([label, count]) =>
    plural(
      count,
      label
        .split(" ")
        .map((word: string) => (/^[A-Z]{2,}$/.test(word) ? word : word.toLowerCase()))
        .join(" "),
    ),
  );
  const unnamed = detail.otherCount - shown.length;
  if (unnamed > 0) named.push(plural(unnamed, named.length > 0 ? "other" : "other transaction", named.length > 0 ? "others" : "other transactions"));
  return named.length > 0 ? named.join(", ") : null;
};

/**
 * An insider alert as a row: who traded and how much in the headline, the
 * largest trade as plain facts, then when it was filed and what else the
 * filing held. Null when there is no buy or sell to lead with (awards only):
 * the row then shows the server's own heading and body.
 */
export const summariseInsider = (detail: InsiderDetail): InsiderSummary | null => {
  const trades = detail.trades.filter((trade) => trade.side !== "other");
  const [largest] = trades;
  if (!largest) return null;
  const tradeCount = detail.buys.count + detail.sells.count;
  const mixed = detail.buys.count > 0 && detail.sells.count > 0;
  const oneInsider = detail.insiders === 1 || tradeCount <= 1;
  // An older alert that can't say how many people traded says "Insiders".
  const who =
    (oneInsider && (largest.role ?? "Insider")) ||
    (detail.insiders !== null && `${integer.format(detail.insiders)} insiders`) ||
    "Insiders";

  const headline: HeadlinePart[] = [{ text: `${who} `, tone: "plain" }];
  if (detail.buys.count > 0) {
    headline.push({ text: "bought ", tone: "plain" });
    headline.push({ text: formatUsd(detail.buys.value), tone: "buy" });
  }
  if (detail.buys.count > 0 && detail.sells.count > 0) headline.push({ text: ", ", tone: "plain" });
  if (detail.sells.count > 0) {
    headline.push({ text: "sold ", tone: "plain" });
    headline.push({ text: formatUsd(detail.sells.value), tone: "sell" });
  }

  const holding =
    largest.owned !== null && largest.owned > 0 ? `holds ${compactHolding.format(largest.owned)} shares` : null;
  // Which way the largest trade went, when the alert holds both.
  const largestTrade = mixed ? `${verbOf(largest)} ${tradeAt(largest)}` : tradeAt(largest);
  let facts: string;
  if (tradeCount <= 1) {
    facts = [largest.name, tradeAt(largest), holding].filter(Boolean).join(" · ");
  } else if (oneInsider) {
    const kind =
      (detail.sells.count === 0 && plural(tradeCount, "purchase")) ||
      (detail.buys.count === 0 && plural(tradeCount, "sale")) ||
      plural(tradeCount, "trade");
    facts = [largest.name, kind, `largest ${largestTrade}`].join(" · ");
  } else {
    facts = [`Largest: ${largest.name}${largest.role ? `, ${largest.role}` : ""}`, largestTrade].join(" · ");
  }

  const filed = detail.filedOn && dayjs(detail.filedOn).isValid() ? `Filed ${dayjs(detail.filedOn).format("D MMM")}` : null;
  const other = otherActivity(detail);
  const footnote = [filed, other ? `also ${other}` : null].filter(Boolean).join(" · ");
  return {
    headline,
    facts,
    footnote: footnote.length > 0 ? `${footnote.charAt(0).toUpperCase()}${footnote.slice(1)}` : null,
  };
};
